import { useEffect, useState } from "react";
import { api, fmtDate, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { Clock4, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Timesheets() {
    const [items, setItems] = useState([]);
    const [projects, setProjects] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ project_id: "", date: today(), hours: 0, description: "", billable: true, rate: 0 });

    const load = async () => {
        const [t, p] = await Promise.all([api.get("/timesheets"), api.get("/projects")]);
        setItems(t.data); setProjects(p.data);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        await api.post("/timesheets", { ...form, hours: Number(form.hours), rate: Number(form.rate) });
        toast.success("Time entry saved");
        setOpen(false);
        await load();
    };

    return (
        <>
            <PageHeader kicker="PROJECTS" title="Timesheets" subtitle="Billable and non-billable time across projects."
                actions={<Button onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" />Log time</Button>} />
            <PageBody>
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {items.length === 0 ? <EmptyState icon={Clock4} title="No time logged" /> :
                        <table className="fin-table">
                            <thead><tr><th>Date</th><th>Project</th><th>User</th><th>Description</th><th className="num">Hours</th><th>Billable</th></tr></thead>
                            <tbody>
                                {items.map((t) => {
                                    const p = projects.find((x) => x.id === t.project_id);
                                    return (
                                        <tr key={t.id}>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(t.date)}</td>
                                            <td>{p?.name || "—"}</td>
                                            <td>{t.user_name}</td>
                                            <td>{t.description}</td>
                                            <td className="num">{t.hours}h</td>
                                            <td>{t.billable ? <span className="text-positive text-[11px]">YES</span> : <span className="text-muted-foreground text-[11px]">NO</span>}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>}
                </div>
            </PageBody>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-[420px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">Log time</SheetTitle></SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-4" onSubmit={submit}>
                        <div><Label>Project</Label>
                            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pick project" /></SelectTrigger>
                                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Hours</Label><Input type="number" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
                        <div><Label>Rate</Label><Input type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="mt-1.5" /></div>
                        <Button type="submit" className="w-full">Save</Button>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}
